import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getApiBase } from "../lib/apiBase";

const STATUS_ORDER = ["pending", "good", "miss"];
const STATS_LIFT_LABELS = {
  squat: "Squat",
  bench: "Bench",
  deadlift: "Deadlift",
};

const toNumberOrNull = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const normalizeProbability = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return null;
  const scaled = parsed > 1.5 ? parsed : parsed * 100;
  return clamp(scaled, 0, 100);
};

const normalizeNameKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const normalizeAttemptStatus = (value) => {
  if (!value) return "";
  const lower = String(value).trim().toLowerCase();
  if (lower.startsWith("good")) return "good";
  if (lower.startsWith("miss") || lower.startsWith("fail") || lower.startsWith("no")) {
    return "miss";
  }
  return "";
};

const getLiveAttemptKey = (nameKey, lift, attemptNo) =>
  `${nameKey}:${lift}:${attemptNo}`;

const buildLiveAttemptIndex = (attempts) => {
  if (!Array.isArray(attempts)) return {};
  const index = {};
  attempts.forEach((attempt) => {
    if (!attempt || typeof attempt !== "object") return;
    const nameKey = normalizeNameKey(attempt.lifter_name);
    const lift = attempt.lift;
    const attemptNo = Number(attempt.attempt);
    if (!nameKey || !lift || !Number.isFinite(attemptNo)) return;
    const key = getLiveAttemptKey(nameKey, lift, attemptNo);
    index[key] = attempt;
  });
  return index;
};

const getLiveAttemptFor = (liveIndex, nameKey, lift, attemptIndex) => {
  if (!liveIndex || !nameKey || !lift) return null;
  const key = getLiveAttemptKey(nameKey, lift, attemptIndex + 1);
  return liveIndex[key] || null;
};

const buildEffectiveAttempts = (attempts, liveIndex, nameKey, lift) =>
  (attempts || []).map((attempt, index) => {
    const liveAttempt = getLiveAttemptFor(liveIndex, nameKey, lift, index);
    const liveStatus = normalizeAttemptStatus(liveAttempt?.status);
    const isFinal = liveStatus === "good" || liveStatus === "miss";
    const liveWeight = toNumberOrNull(liveAttempt?.weight);
    const storedWeight = toNumberOrNull(attempt?.weight);
    return {
      weight: isFinal ? liveWeight : storedWeight,
      status: isFinal ? liveStatus : "pending",
    };
  });

const getStatsProbability = (stats, lift, index) => {
  if (!stats || typeof stats !== "object") return null;
  const label = STATS_LIFT_LABELS[lift];
  if (!label) return null;
  const key = `Pct successful ${label} ${index + 1}`;
  return normalizeProbability(stats[key]);
};

const isDraftNumber = (value) => {
  if (typeof value !== "string") return false;
  if (value === "") return false;
  if (!/^-?\d*\.?\d*$/.test(value)) return false;
  return toNumberOrNull(value) === null;
};

const normalizeAttempt = (attempt) => {
  const existing = attempt && typeof attempt === "object" ? attempt : {};
  const rawWeight =
    existing.weight ??
    existing.actual ??
    existing.predicted ??
    existing.target ??
    existing.pred ??
    null;
  const parsedWeight = toNumberOrNull(rawWeight);
  let weight = null;
  if (parsedWeight !== null && parsedWeight !== 0) {
    weight = parsedWeight;
  } else if (isDraftNumber(rawWeight)) {
    weight = rawWeight;
  }
  const resolvedProbability = normalizeProbability(existing.probability);
  return {
    weight,
    probability: resolvedProbability,
    status: STATUS_ORDER.includes(existing.status) ? existing.status : "pending",
    manual_override: Boolean(existing.manual_override),
    manual_override_live: toNumberOrNull(existing.manual_override_live),
  };
};

const getLiftStatValue = (stats, key) =>
  stats && typeof stats === "object" ? toNumberOrNull(stats[key]) : null;

const getCompBest = (stats, liftKey) => {
  const label = STATS_LIFT_LABELS[liftKey];
  if (!label) return null;
  return getLiftStatValue(stats, `Best ${label} Kg`);
};

const getAttemptWeightValue = (attempt) => {
  if (!attempt || typeof attempt !== "object") return null;
  const parsed = toNumberOrNull(attempt.weight);
  if (parsed === null || parsed === 0) return null;
  return Math.abs(parsed);
};

const getLiftForecast = (liftKey, attempts, stats) => {
  const label = STATS_LIFT_LABELS[liftKey];
  if (!label) {
    return {
      suggestions: [null, null, null],
      totals: [null, null, null],
    };
  }
  const best = getLiftStatValue(stats, `Best ${label} Kg`);
  const inc13 = getLiftStatValue(stats, `Increase ${label} 1_to_3 Kg`) ?? 0;
  const inc12 = getLiftStatValue(stats, `Increase ${label} 1_to_2 Kg`) ?? 0;
  const inc23 = getLiftStatValue(stats, `Increase ${label} 2_to_3 Kg`) ?? 0;

  const opener = best !== null ? best - inc13 : null;
  const weight1 = getAttemptWeightValue(attempts?.[0]);
  const base1 = weight1 ?? opener;
  const second = base1 !== null ? base1 + inc12 : null;
  const weight2 = getAttemptWeightValue(attempts?.[1]);
  const base2 = weight2 ?? second;
  const third = base2 !== null ? base2 + inc23 : null;
  const weight3 = getAttemptWeightValue(attempts?.[2]);

  return {
    suggestions: [opener, second, third],
    totals: [weight1 ?? opener, weight2 ?? second, weight3 ?? third],
  };
};

const isFinalAttempt = (attempt) =>
  attempt?.status === "good" || attempt?.status === "miss";

const isHalfStep = (value) =>
  Number.isFinite(value) && Math.abs(value * 2 - Math.round(value * 2)) < 1e-6;

const isValidAttemptWeight = (value, attemptIndex, previousValue) => {
  if (!Number.isFinite(value)) return false;
  if (value <= 25) return false;
  if (!isHalfStep(value)) return false;
  const hasPrevious =
    Number.isInteger(attemptIndex) && attemptIndex > 0 && Number.isFinite(previousValue);
  if (hasPrevious && value < previousValue) return false;
  return true;
};

const isSuspiciousOpener = (liftKey, attemptIndex, value, predictionValue) => {
  if (liftKey !== "bench" && liftKey !== "deadlift") return false;
  if (attemptIndex !== 0) return false;
  if (!Number.isFinite(value) || value <= 0) return false;
  if (!Number.isFinite(predictionValue) || predictionValue <= 0) return false;
  return value > predictionValue * 1.1;
};

const normalizeLift = (lift, stats, liftKey) => {
  const list = Array.isArray(lift) ? lift : [];
  return [0, 1, 2].map((index) => {
    const existing = list[index];
    const statsProbability = getStatsProbability(stats, liftKey, index);
    const merged =
      existing && typeof existing === "object" ? existing : {};
    const withProbability =
      merged.probability === undefined || merged.probability === null
        ? statsProbability === null
          ? merged
          : { ...merged, probability: statsProbability }
        : merged;
    return normalizeAttempt(withProbability);
  });
};

const normalizeAthlete = (athlete) => {
  const entry = athlete && typeof athlete === "object" ? athlete : {};
  const runsheet = entry.runsheet && typeof entry.runsheet === "object" ? entry.runsheet : {};
  const stats = entry.stats && typeof entry.stats === "object" ? entry.stats : null;
  return {
    ...entry,
    runsheet: {
      targetPosition: normalizeTargetPosition(
        runsheet.targetPosition ?? entry.targetPosition ?? null
      ),
      pinned: Boolean(runsheet.pinned || entry.pinned || entry.is_pinned),
      squat: normalizeLift(runsheet.squat, stats, "squat"),
      bench: normalizeLift(runsheet.bench, stats, "bench"),
      deadlift: normalizeLift(runsheet.deadlift, stats, "deadlift"),
    },
  };
};

const getAthleteKey = (athlete, fallback) =>
  athlete?.id || athlete?.openpowerlifting_id || athlete?.name || fallback;

const getWeightClassLabel = (athlete) => {
  const raw = athlete?.weight_class || athlete?.weightClass || "Open";
  return String(raw).toUpperCase();
};

const normalizeFilterValue = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  return text;
};

const getSessionLabel = (athlete) =>
  normalizeFilterValue(athlete?.session ?? athlete?.session_id);

const getFlightLabel = (athlete) =>
  normalizeFilterValue(athlete?.flight ?? athlete?.flight_id);

const getWeightClassOrder = (label) => {
  const match = label.match(/[\d.]+/);
  if (!match) return 9999;
  const parsed = Number.parseFloat(match[0]);
  return Number.isNaN(parsed) ? 9999 : parsed;
};

const getThirdProjected = (attempts, forecast) => {
  const raw = attempts?.[2]?.weight;
  const parsed = toNumberOrNull(raw);
  if (parsed !== null && parsed !== 0) return parsed;
  const fallback = toNumberOrNull(forecast?.suggestions?.[2]);
  return fallback ?? 0;
};

const getBestActual = (attempts) => {
  const goodAttempts = (attempts || [])
    .filter((attempt) => attempt?.status === "good")
    .map((attempt) => toNumberOrNull(attempt.weight))
    .filter((value) => value !== null && value > 0);
  if (goodAttempts.length) {
    return Math.max(...goodAttempts);
  }
  return Math.max(
    0,
    ...(attempts || [])
      .map((attempt) => toNumberOrNull(attempt.weight))
      .filter((value) => value !== null && value > 0)
  );
};

const getBestAttemptDisplay = (attempts) => {
  const normalized = (attempts || [])
    .map((attempt) => ({
      weight: toNumberOrNull(attempt?.weight),
      status: attempt?.status || "pending",
    }))
    .filter((attempt) => attempt.weight !== null && attempt.weight !== 0);

  if (!normalized.length) {
    return { weight: null, status: "pending" };
  }

  const good = normalized.filter((attempt) => attempt.status === "good");
  if (good.length) {
    let best = good[0];
    good.forEach((attempt) => {
      if (attempt.weight > best.weight) {
        best = attempt;
      }
    });
    return { weight: best.weight, status: "good" };
  }

  let best = normalized[0];
  normalized.forEach((attempt) => {
    if (attempt.weight > best.weight) {
      best = attempt;
    }
  });
  return {
    weight: best.weight,
    status: best.status === "miss" ? "miss" : "pending",
  };
};

const getOpenPowerliftingId = (athlete) => {
  if (athlete && typeof athlete === "object") {
    return athlete.openpowerlifting_id || athlete.openPowerliftingId || "";
  }
  return "";
};

const hasAthleteStats = (athlete) => {
  if (!athlete || typeof athlete !== "object") return false;
  const stats = athlete.stats;
  if (!stats) return false;
  if (Array.isArray(stats)) return stats.length > 0;
  if (typeof stats === "string") return stats.trim().length > 0;
  if (typeof stats === "object") return Object.keys(stats).length > 0;
  return false;
};

function normalizeTargetPosition(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

const getDivisionKey = (athlete) => {
  const raw =
    athlete?.division ||
    athlete?.division_name ||
    athlete?.divisionName ||
    "";
  if (!raw || typeof raw !== "string") return "OPEN";
  const trimmed = raw.trim();
  return trimmed ? trimmed.toUpperCase() : "OPEN";
};

const buildRankLadders = (athletes, getValue, minValue = 0) => {
  const grouped = new Map();
  athletes.forEach((athlete) => {
    const value = getValue(athlete);
    if (value <= minValue) return;
    const key = athlete.rankGroupKey || athlete.weightClassLabel;
    const group = grouped.get(key) || [];
    group.push(value);
    grouped.set(key, group);
  });

  const ladders = new Map();
  grouped.forEach((values, key) => {
    const sorted = [...values].sort((a, b) => b - a);
    const ranks = [];
    let lastValue = null;
    sorted.forEach((value, index) => {
      if (lastValue === null || value !== lastValue) {
        ranks.push({ rank: index + 1, value });
        lastValue = value;
      }
    });
    const byRank = new Map(ranks.map((entry) => [entry.rank, entry.value]));
    ladders.set(key, { ranks, byRank });
  });
  return ladders;
};

const getTargetDelta = (ladders, athlete, position, currentValue, currentRank) => {
  if (!position) return null;
  const ladder = ladders.get(athlete.rankGroupKey || athlete.weightClassLabel);
  if (!ladder || !ladder.ranks.length) return null;
  if (currentValue === null || currentValue === undefined) return null;

  if (currentRank && currentRank <= position) {
    const currentIndex = ladder.ranks.findIndex((entry) => entry.rank === currentRank);
    if (currentIndex === -1) return null;
    const nextEntry = ladder.ranks[currentIndex + 1];
    if (!nextEntry) return null;
    return currentValue - nextEntry.value;
  }

  const targetValue = ladder.byRank.get(position);
  if (targetValue === undefined) return null;
  return currentValue - targetValue;
};

const buildRankMap = (athletes, getTotal, minTotal = 0) => {
  const grouped = new Map();
  athletes.forEach((athlete) => {
    const total = getTotal(athlete);
    if (total <= minTotal) return;
    const label = athlete.rankGroupKey || athlete.weightClassLabel;
    const group = grouped.get(label) || [];
    group.push({ athlete, total });
    grouped.set(label, group);
  });

  const ranks = {};
  grouped.forEach((group) => {
    const sorted = [...group].sort((a, b) => b.total - a.total);
    let lastTotal = null;
    let rank = 0;
    sorted.forEach((entry, index) => {
      if (lastTotal === null || entry.total !== lastTotal) {
        rank = index + 1;
        lastTotal = entry.total;
      }
      ranks[entry.athlete.key] = rank;
    });
  });
  return ranks;
};

const formatWeight = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "--";
  return Number.isInteger(value) ? `${value}` : value.toFixed(1);
};

const formatDelta = (value) => {
  if (value === null || value === undefined) return "--";
  if (value === 0) return "0";
  const absValue = Math.abs(value);
  const formatted = Number.isInteger(absValue) ? `${absValue}` : absValue.toFixed(1);
  return `${value > 0 ? "+" : "-"}${formatted}`;
};

const getDeltaTone = (value) => {
  if (value === null || value === undefined) return "neutral";
  if (value > 0) return "positive";
  if (value < 0) return "negative";
  return "neutral";
};

const formatProbability = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return `${Math.round(value)}%`;
};

const getProbabilityTone = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const pct = Math.round(value);
  if (pct >= 90) return "green";
  if (pct >= 80) return "olive";
  if (pct >= 65) return "yellow";
  if (pct >= 45) return "orange";
  return "red";
};

const formatAthleteName = (name) => {
  if (!name || typeof name !== "string") return "Unknown lifter";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  const first = parts[0];
  const lastInitial = parts[parts.length - 1]?.charAt(0) || "";
  return `${first} ${lastInitial}.`;
};

function RunSheet() {
  const { meetId } = useParams();
  const navigate = useNavigate();
  const apiBase = useMemo(() => getApiBase(), []);
  const [meet, setMeet] = useState(null);
  const [saveIndicator, setSaveIndicator] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const headInnerRef = useRef(null);
  const liveSinceRef = useRef(null);
  const liveAbortRef = useRef(null);
  const liveRetryRef = useRef(null);
  const [liveAttemptIndex, setLiveAttemptIndex] = useState({});
  const [liveCurrentAttempt, setLiveCurrentAttempt] = useState(null);
  const hasHydratedRef = useRef(false);
  const lastSavedRef = useRef("");
  const latestPayloadRef = useRef("");
  const autoSaveTimerRef = useRef(null);
  const saveAbortRef = useRef(null);
  const [collapsedLifts, setCollapsedLifts] = useState({
    squat: null,
    bench: null,
    deadlift: null,
  });
  const [weightClassFilter, setWeightClassFilter] = useState(() => {
    if (typeof window === "undefined") return "all";
    try {
      return window.localStorage.getItem("runsheet.weightClassFilter") || "all";
    } catch (_err) {
      return "all";
    }
  });
  const [sessionFilter, setSessionFilter] = useState("all");
  const [flightFilter, setFlightFilter] = useState("all");

  const toggleLiftCollapse = useCallback((lift) => {
    setCollapsedLifts((prev) => {
      const current = prev[lift];
      let nextValue;
      if (current === null || current === undefined) {
        nextValue = true;
      } else if (current === true) {
        nextValue = false;
      } else {
        nextValue = null;
      }
      return {
        ...prev,
        [lift]: nextValue,
      };
    });
  }, []);

  useEffect(() => {
    let isActive = true;
    const loadMeet = async () => {
      setIsLoading(true);
      setError("");
      try {
        const response = await fetch(`${apiBase}/meet/${meetId}`, {
          method: "GET",
          credentials: "include",
        });

        if (response.status === 401) {
          navigate("/login", { replace: true });
          return;
        }

        const data = await response.json();
        if (!response.ok || data?.error) {
          throw new Error(data?.error || "Failed to load meet.");
        }

        if (isActive) {
          const loaded = data.meet || {};
          const athletes = Array.isArray(loaded.athletes)
            ? loaded.athletes.map(normalizeAthlete)
            : [];
          setMeet({ ...loaded, athletes });
          setSaveIndicator("Saved");
        }
      } catch (err) {
        if (isActive) {
          setError(err?.message || "Something went wrong.");
          setSaveIndicator("");
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadMeet();

    return () => {
      isActive = false;
    };
  }, [apiBase, meetId, navigate]);

  const liftingcastMeetId = useMemo(() => {
    const source = meet?.link || "";
    if (!source || typeof source !== "string") return "";
    const patterns = [/\/m\/([A-Za-z0-9_-]+)/, /\/meet\/([A-Za-z0-9_-]+)/, /\/meets\/([A-Za-z0-9_-]+)/];
    for (const pattern of patterns) {
      const match = source.match(pattern);
      if (match) return match[1];
    }
    return "";
  }, [meet?.link]);

  const applyLiveAttempts = useCallback((attempts, replace) => {
    if (!Array.isArray(attempts) || attempts.length === 0) return;
    const nextIndex = buildLiveAttemptIndex(attempts);
    setLiveAttemptIndex((prev) => (replace ? nextIndex : { ...prev, ...nextIndex }));
  }, []);

  useEffect(() => {
    if (!liftingcastMeetId) return undefined;
    let isActive = true;
    let retryDelay = 1500;

    const fetchAttempts = async (body) => {
      if (liveAbortRef.current) {
        liveAbortRef.current.abort();
      }
      const controller = new AbortController();
      liveAbortRef.current = controller;
      const response = await fetch(
        `${apiBase}/liftingcast/meet/${liftingcastMeetId}/attempts`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          signal: controller.signal,
          body: JSON.stringify(body),
        }
      );
      if (response.status === 401) {
        navigate("/login", { replace: true });
        return null;
      }
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || "Failed to fetch live attempts.");
      }
      return data;
    };

    const startLongpoll = async () => {
      if (!isActive) return;
      try {
        const data = await fetchAttempts({
          since: liveSinceRef.current || 0,
          limit: 2000,
          feed: "longpoll",
          timeout: 25000,
        });
        if (!isActive || !data) return;
        if (data.since) {
          liveSinceRef.current = data.since;
        }
        if (Array.isArray(data.attempts) && data.attempts.length) {
          applyLiveAttempts(data.attempts, false);
        }
        if (Object.prototype.hasOwnProperty.call(data, "current_attempt")) {
          setLiveCurrentAttempt(data.current_attempt || null);
        }
        retryDelay = 1500;
        startLongpoll();
      } catch (err) {
        if (!isActive || err?.name === "AbortError") return;
        liveRetryRef.current = setTimeout(startLongpoll, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 15000);
      }
    };

    const fetchSnapshot = async () => {
      try {
        const data = await fetchAttempts({ since: 0, limit: 2000 });
        if (!isActive || !data) return;
        if (Array.isArray(data.attempts) && data.attempts.length) {
          applyLiveAttempts(data.attempts, true);
        }
        if (Object.prototype.hasOwnProperty.call(data, "current_attempt")) {
          setLiveCurrentAttempt(data.current_attempt || null);
        }
        if (data.since) {
          liveSinceRef.current = data.since;
        }
        startLongpoll();
      } catch (err) {
        if (!isActive || err?.name === "AbortError") return;
        liveRetryRef.current = setTimeout(fetchSnapshot, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 15000);
      }
    };

    fetchSnapshot();

    return () => {
      isActive = false;
      if (liveAbortRef.current) {
        liveAbortRef.current.abort();
      }
      if (liveRetryRef.current) {
        clearTimeout(liveRetryRef.current);
        liveRetryRef.current = null;
      }
    };
  }, [apiBase, applyLiveAttempts, liftingcastMeetId, navigate]);

  const updateAthlete = useCallback((key, updater) => {
    setMeet((prev) => {
      if (!prev) return prev;
      const updated = prev.athletes.map((athlete, index) => {
        const athleteKey = getAthleteKey(athlete, `athlete-${index}`);
        if (athleteKey !== key) return athlete;
        return normalizeAthlete(updater(athlete));
      });
      return { ...prev, athletes: updated };
    });
  }, []);

  const buildPayload = useCallback(
    (value) => ({
      name: value?.name || "",
      date: value?.date || "",
      link: value?.link || "",
      athletes: value?.athletes || [],
    }),
    []
  );

  const saveMeet = useCallback(
    async ({ payload, payloadKey }) => {
      if (!payload) return;
      if (saveAbortRef.current) {
        saveAbortRef.current.abort();
      }
      const controller = new AbortController();
      saveAbortRef.current = controller;
      setSaveIndicator("Saving...");
      setError("");

      try {
        const response = await fetch(`${apiBase}/meet/${meetId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          signal: controller.signal,
          body: JSON.stringify(payload),
        });

        if (response.status === 401) {
          navigate("/login", { replace: true });
          return;
        }

        const data = await response.json();
        if (!response.ok || data?.error) {
          throw new Error(data?.error || "Failed to save run sheet.");
        }

        if (latestPayloadRef.current !== payloadKey) {
          return;
        }
        lastSavedRef.current = payloadKey;
        setMeet((prev) => {
          if (!prev) return prev;
          const updated = data.meet || prev;
          const prevByKey = new Map(
            (prev.athletes || []).map((athlete, index) => [
              getAthleteKey(athlete, `athlete-${index}`),
              athlete,
            ])
          );
          const athletes = Array.isArray(updated.athletes)
            ? updated.athletes.map((athlete, index) => {
                const key = getAthleteKey(athlete, `athlete-${index}`);
                const prevAthlete = prevByKey.get(key);
                const nextStats =
                  athlete?.stats && typeof athlete.stats === "object"
                    ? athlete.stats
                    : prevAthlete?.stats;
                const merged = nextStats ? { ...athlete, stats: nextStats } : athlete;
                return normalizeAthlete(merged);
              })
            : prev.athletes;
          return { ...prev, ...updated, athletes };
        });
        setSaveIndicator("Saved");
      } catch (err) {
        if (err?.name === "AbortError") return;
        setSaveIndicator("Save failed");
        setError(err?.message || "Something went wrong.");
      } finally {
        if (saveAbortRef.current === controller) {
          saveAbortRef.current = null;
        }
      }
    },
    [apiBase, meetId, navigate]
  );

  useEffect(() => {
    if (!meet || isLoading) return;
    const payload = buildPayload(meet);
    const payloadKey = JSON.stringify(payload);
    latestPayloadRef.current = payloadKey;
    if (!hasHydratedRef.current) {
      hasHydratedRef.current = true;
      lastSavedRef.current = payloadKey;
      setSaveIndicator("Saved");
      return;
    }
    if (payloadKey === lastSavedRef.current) {
      setSaveIndicator("Saved");
      return;
    }
    setSaveIndicator("Unsaved");
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      saveMeet({ payload, payloadKey });
    }, 3000);
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [buildPayload, isLoading, meet, saveMeet]);

  const handleBodyScroll = useCallback((event) => {
    if (!headInnerRef.current) return;
    headInnerRef.current.style.transform = `translateX(${-event.currentTarget.scrollLeft}px)`;
  }, []);

  const { rankedAthletes, liftCompletion } = useMemo(() => {
    if (!meet) {
      return {
        rankedAthletes: [],
        liftCompletion: { squat: false, bench: false, deadlift: false },
      };
    }
    const base = meet.athletes.map((athlete, index) => {
      const key = getAthleteKey(athlete, `athlete-${index}`);
      const weightClassLabel = getWeightClassLabel(athlete);
      const nameKey = normalizeNameKey(athlete?.name);
      const rankGroupKey = `${getDivisionKey(athlete)}|${weightClassLabel}`;
      const runsheet = athlete.runsheet || {};
      const squat = runsheet.squat || [];
      const bench = runsheet.bench || [];
      const deadlift = runsheet.deadlift || [];
      const squatActual = buildEffectiveAttempts(
        squat,
        liveAttemptIndex,
        nameKey,
        "squat"
      );
      const benchActual = buildEffectiveAttempts(
        bench,
        liveAttemptIndex,
        nameKey,
        "bench"
      );
      const deadliftActual = buildEffectiveAttempts(
        deadlift,
        liveAttemptIndex,
        nameKey,
        "deadlift"
      );
      const squatForecast = getLiftForecast("squat", squatActual, athlete.stats);
      const benchForecast = getLiftForecast("bench", benchActual, athlete.stats);
      const deadliftForecast = getLiftForecast("deadlift", deadliftActual, athlete.stats);
      const squatComplete = squatActual.every(isFinalAttempt);
      const benchComplete = benchActual.every(isFinalAttempt);
      const deadliftComplete = deadliftActual.every(isFinalAttempt);
      const bestSquat = getBestActual(squatActual);
      const bestBench = getBestActual(benchActual);
      const bestDeadlift = getBestActual(deadliftActual);
      const bestSquatAttempt = getBestAttemptDisplay(squatActual);
      const bestBenchAttempt = getBestAttemptDisplay(benchActual);
      const bestDeadliftAttempt = getBestAttemptDisplay(deadliftActual);
      const squatThirdComplete = isFinalAttempt(squatActual?.[2]);
      const benchThirdComplete = isFinalAttempt(benchActual?.[2]);
      const deadliftThirdComplete = isFinalAttempt(deadliftActual?.[2]);
      const predictedSquat = squatThirdComplete
        ? getBestActual(squatActual)
        : getThirdProjected(squatActual, squatForecast);
      const predictedBench = benchThirdComplete
        ? getBestActual(benchActual)
        : getThirdProjected(benchActual, benchForecast);
      const predictedDeadlift = deadliftThirdComplete
        ? getBestActual(deadliftActual)
        : getThirdProjected(deadliftActual, deadliftForecast);
      const predictedSubtotal =
        predictedSquat +
        predictedBench;
      const actualSubtotal =
        getBestActual(squatActual) +
        getBestActual(benchActual);
      const predictedTotal =
        predictedSubtotal +
        predictedDeadlift;
      const actualTotal =
        actualSubtotal +
        getBestActual(deadliftActual);
      const rankTotal = predictedTotal;
      return {
        ...athlete,
        key,
        weightClassLabel,
        weightClassOrder: getWeightClassOrder(weightClassLabel),
        rankGroupKey,
        predictedTotal,
        actualTotal,
        predictedSubtotal,
        actualSubtotal,
        bestSquat,
        bestBench,
        bestDeadlift,
        bestSquatAttempt,
        bestBenchAttempt,
        bestDeadliftAttempt,
        squatComplete,
        benchComplete,
        deadliftComplete,
        rankTotal,
        nameKey,
        squatForecast,
        benchForecast,
        deadliftForecast,
      };
    });

    const liftCompletion = {
      squat: base.length > 0 && base.every((athlete) => athlete.squatComplete),
      bench: base.length > 0 && base.every((athlete) => athlete.benchComplete),
      deadlift: base.length > 0 && base.every((athlete) => athlete.deadliftComplete),
    };

    const subtotalActualRanks = buildRankMap(
      base,
      (athlete) => athlete.actualSubtotal,
      1
    );
    const subtotalPredRanks = buildRankMap(
      base,
      (athlete) => athlete.predictedSubtotal
    );
    const totalActualRanks = buildRankMap(base, (athlete) => athlete.actualTotal, 1);
    const totalPredRanks = buildRankMap(base, (athlete) => athlete.predictedTotal);

    const subtotalActualLadders = buildRankLadders(
      base,
      (athlete) => athlete.actualSubtotal,
      1
    );
    const subtotalPredLadders = buildRankLadders(
      base,
      (athlete) => athlete.predictedSubtotal
    );
    const totalActualLadders = buildRankLadders(
      base,
      (athlete) => athlete.actualTotal,
      1
    );
    const totalPredLadders = buildRankLadders(
      base,
      (athlete) => athlete.predictedTotal
    );

    const withTargets = base.map((athlete) => {
      const position = normalizeTargetPosition(athlete.runsheet?.targetPosition);
      const subtotalLiveRank = subtotalActualRanks[athlete.key] || null;
      const subtotalPredRank = subtotalPredRanks[athlete.key] || null;
      const totalLiveRank = totalActualRanks[athlete.key] || null;
      const totalPredRank = totalPredRanks[athlete.key] || null;
      return {
        ...athlete,
        targetPosition: position,
        targetDeltaSubtotalLive: getTargetDelta(
          subtotalActualLadders,
          athlete,
          position,
          athlete.actualSubtotal,
          subtotalLiveRank
        ),
        targetDeltaSubtotalPred: getTargetDelta(
          subtotalPredLadders,
          athlete,
          position,
          athlete.predictedSubtotal,
          subtotalPredRank
        ),
        targetDeltaTotalLive: getTargetDelta(
          totalActualLadders,
          athlete,
          position,
          athlete.actualTotal,
          totalLiveRank
        ),
        targetDeltaTotalPred: getTargetDelta(
          totalPredLadders,
          athlete,
          position,
          athlete.predictedTotal,
          totalPredRank
        ),
      };
    });

    const predictedRanks = totalPredRanks;
    const actualRanks = totalActualRanks;

    const enriched = withTargets.map((athlete) => ({
      ...athlete,
      predictedRank: predictedRanks[athlete.key] || null,
      actualRank: athlete.actualTotal > 0 ? actualRanks[athlete.key] || null : null,
      sortRank: predictedRanks[athlete.key] || 9999,
    }));

    const sorted = enriched.sort((a, b) => {
      const aPinned = Boolean(a.runsheet?.pinned);
      const bPinned = Boolean(b.runsheet?.pinned);
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      if (a.weightClassOrder !== b.weightClassOrder) {
        return a.weightClassOrder - b.weightClassOrder;
      }
      if (a.sortRank !== b.sortRank) return a.sortRank - b.sortRank;
      if (b.rankTotal !== a.rankTotal) return b.rankTotal - a.rankTotal;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });

    let lastPinnedIndex = -1;
    sorted.forEach((athlete, index) => {
      if (athlete.runsheet?.pinned) {
        lastPinnedIndex = index;
      }
    });

    const withPinned = sorted.map((athlete, index) => ({
      ...athlete,
      isLastPinned: index === lastPinnedIndex,
    }));

    return { rankedAthletes: withPinned, liftCompletion };
  }, [liveAttemptIndex, meet]);

  const collapseFlags = useMemo(() => {
    const resolve = (lift) => {
      const override = collapsedLifts[lift];
      if (override === null || override === undefined) {
        return liftCompletion[lift];
      }
      return override;
    };
    return {
      squat: resolve("squat"),
      bench: resolve("bench"),
      deadlift: resolve("deadlift"),
    };
  }, [collapsedLifts, liftCompletion]);

  const runSheetStyle = useMemo(
    () => ({
      "--runsheet-squat-col": collapseFlags.squat
        ? "var(--runsheet-lift-col-collapsed)"
        : "var(--runsheet-lift-col)",
      "--runsheet-bench-col": collapseFlags.bench
        ? "var(--runsheet-lift-col-collapsed)"
        : "var(--runsheet-lift-col)",
      "--runsheet-deadlift-col": collapseFlags.deadlift
        ? "var(--runsheet-lift-col-collapsed)"
        : "var(--runsheet-lift-col)",
    }),
    [collapseFlags]
  );

  const currentAttemptKey = useMemo(() => {
    if (!liveCurrentAttempt || typeof liveCurrentAttempt !== "object") {
      return null;
    }
    const lift = liveCurrentAttempt.lift;
    const attemptNo = Number(liveCurrentAttempt.attempt);
    const nameKey = normalizeNameKey(liveCurrentAttempt.lifter_name);
    if (!lift || !Number.isFinite(attemptNo) || !nameKey) return null;
    return { nameKey, lift, attempt: attemptNo };
  }, [liveCurrentAttempt]);

  const isNumericFilter = useCallback((value) => /^-?\d+(\.\d+)?$/.test(value), []);

  const weightClassOptions = useMemo(() => {
    if (!meet || !Array.isArray(meet.athletes)) return [];
    const labels = meet.athletes.map((athlete) => getWeightClassLabel(athlete));
    const parseLabel = (label) => {
      const text = String(label || "");
      const match = text.match(/([\d.]+)/);
      const value = match ? Number.parseFloat(match[1]) : Number.NaN;
      const plus = /\+/.test(text);
      return {
        raw: text,
        value,
        plus,
      };
    };
    return Array.from(new Set(labels)).sort((a, b) => {
      const aParsed = parseLabel(a);
      const bParsed = parseLabel(b);
      const aHas = Number.isFinite(aParsed.value);
      const bHas = Number.isFinite(bParsed.value);
      if (aHas && bHas && aParsed.value !== bParsed.value) {
        return aParsed.value - bParsed.value;
      }
      if (aHas && bHas && aParsed.plus !== bParsed.plus) {
        return aParsed.plus ? 1 : -1;
      }
      if (aHas !== bHas) return aHas ? -1 : 1;
      return aParsed.raw.localeCompare(bParsed.raw);
    });
  }, [meet]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem("runsheet.weightClassFilter", weightClassFilter);
    } catch (_err) {
      // Ignore storage errors.
    }
  }, [weightClassFilter]);

  useEffect(() => {
    if (weightClassFilter === "all") return;
    if (weightClassOptions.length === 0) return;
    if (!weightClassOptions.includes(weightClassFilter)) {
      setWeightClassFilter("all");
    }
  }, [weightClassFilter, weightClassOptions]);

  const sessionOptions = useMemo(() => {
    if (!meet || !Array.isArray(meet.athletes)) return [];
    const values = meet.athletes
      .map((athlete) => getSessionLabel(athlete))
      .filter(Boolean);
    return Array.from(new Set(values)).sort((a, b) => {
      const aIsNum = isNumericFilter(a);
      const bIsNum = isNumericFilter(b);
      if (aIsNum && bIsNum) return Number(a) - Number(b);
      if (aIsNum !== bIsNum) return aIsNum ? -1 : 1;
      return a.localeCompare(b);
    });
  }, [isNumericFilter, meet]);

  const flightOptions = useMemo(() => {
    if (!meet || !Array.isArray(meet.athletes)) return [];
    const values = meet.athletes
      .map((athlete) => getFlightLabel(athlete))
      .filter(Boolean);
    return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
  }, [meet]);

  const visibleAthletes = useMemo(() => {
    let filtered = rankedAthletes;
    if (weightClassFilter !== "all") {
      filtered = filtered.filter(
        (athlete) => athlete.weightClassLabel === weightClassFilter
      );
    }
    if (sessionFilter !== "all") {
      filtered = filtered.filter(
        (athlete) => getSessionLabel(athlete) === sessionFilter
      );
    }
    if (flightFilter !== "all") {
      filtered = filtered.filter(
        (athlete) => getFlightLabel(athlete) === flightFilter
      );
    }
    return filtered;
  }, [flightFilter, rankedAthletes, sessionFilter, weightClassFilter]);

  if (isLoading) {
    return <div className="status">Loading run sheet...</div>;
  }

  if (!meet) {
    return <div className="error">Meet not found.</div>;
  }

  return (
    <div className="run-sheet" style={runSheetStyle}>
      <header className="run-sheet-header">
        <button type="button" className="back-link" onClick={() => navigate("/")}>
          ←
        </button>
        <div className="run-sheet-title">
          <p className="eyebrow">Run Sheet</p>
          <h2>
            <span className="run-sheet-title-row">
              <span>{meet.name || "Untitled meet"}</span>
              {liftingcastMeetId ? (
                <a
                  className="run-sheet-link"
                  href={`https://liftingcast.com/meets/${liftingcastMeetId}`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open Liftingcast meet"
                >
                  ↗
                </a>
              ) : null}
            </span>
          </h2>
          <p className="subtle">
            {meet.date ? `Meet day: ${meet.date}` : "Meet day not set"}
          </p>
        </div>
        <div className="run-sheet-actions">
          <button
            type="button"
            className="link-button"
            onClick={() => navigate(`/meet/${meetId}`)}
          >
            Edit meet
          </button>
          <div
            className={`run-sheet-save-state ${
              saveIndicator === "Saving..."
                ? "saving"
                : saveIndicator === "Saved"
                ? "saved"
                : saveIndicator === "Save failed"
                ? "error"
                : saveIndicator === "Unsaved"
                ? "unsaved"
                : ""
            }`}
          >
            {saveIndicator}
          </div>
        </div>
      </header>

      <div className="run-sheet-filters">
        <label className="filter-field">
          <span>Session</span>
          <select
            value={sessionFilter}
            onChange={(event) => setSessionFilter(event.target.value)}
          >
            <option value="all">All</option>
            {sessionOptions.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-field">
          <span>Flight</span>
          <select
            value={flightFilter}
            onChange={(event) => setFlightFilter(event.target.value)}
          >
            <option value="all">All</option>
            {flightOptions.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="filter-field">
          <span>Weight class</span>
          <select
            value={weightClassFilter}
            onChange={(event) => setWeightClassFilter(event.target.value)}
          >
            <option value="all">All</option>
            {weightClassOptions.map((label) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <section className="run-sheet-table">
        <div className="run-sheet-head-wrap">
          <div className="run-sheet-head">
            <div className="run-sheet-head-track" ref={headInnerRef}>
              <div className="run-sheet-cell rank">Rank</div>
              <div className="run-sheet-cell athlete">Athlete</div>
              <div className="run-sheet-cell lift">
                <div className="lift-title">
                  <button
                    type="button"
                    className={`lift-toggle${collapseFlags.squat ? " active" : ""}`}
                    onClick={() => toggleLiftCollapse("squat")}
                    aria-label="Toggle squat collapse"
                    title={collapseFlags.squat ? "Show all squat attempts" : "Collapse squat"}
                  >
                    <span className="lift-toggle-icon">
                      {collapseFlags.squat ? "◀▶" : "▶◀"}
                    </span>
                  </button>
                  <span>Squat</span>
                </div>
                <div className={`lift-grid${collapseFlags.squat ? " compact" : ""}`}>
                  {collapseFlags.squat ? (
                    <span>Best</span>
                  ) : (
                    <>
                      <span>SQ1</span>
                      <span>SQ2</span>
                      <span>SQ3</span>
                    </>
                  )}
                </div>
              </div>
              <div className="run-sheet-cell lift">
                <div className="lift-title">
                  <button
                    type="button"
                    className={`lift-toggle${collapseFlags.bench ? " active" : ""}`}
                    onClick={() => toggleLiftCollapse("bench")}
                    aria-label="Toggle bench collapse"
                    title={collapseFlags.bench ? "Show all bench attempts" : "Collapse bench"}
                  >
                    <span className="lift-toggle-icon">
                      {collapseFlags.bench ? "◀▶" : "▶◀"}
                    </span>
                  </button>
                  <span>Bench</span>
                </div>
                <div className={`lift-grid${collapseFlags.bench ? " compact" : ""}`}>
                  {collapseFlags.bench ? (
                    <span>Best</span>
                  ) : (
                    <>
                      <span>BP1</span>
                      <span>BP2</span>
                      <span>BP3</span>
                    </>
                  )}
                </div>
              </div>
              <div className="run-sheet-cell subtotal">Subtotal</div>
              <div className="run-sheet-cell lift">
                <div className="lift-title">
                  <button
                    type="button"
                    className={`lift-toggle${collapseFlags.deadlift ? " active" : ""}`}
                    onClick={() => toggleLiftCollapse("deadlift")}
                    aria-label="Toggle deadlift collapse"
                    title={collapseFlags.deadlift ? "Show all deadlift attempts" : "Collapse deadlift"}
                  >
                    <span className="lift-toggle-icon">
                      {collapseFlags.deadlift ? "◀▶" : "▶◀"}
                    </span>
                  </button>
                  <span>Deadlift</span>
                </div>
                <div className={`lift-grid${collapseFlags.deadlift ? " compact" : ""}`}>
                  {collapseFlags.deadlift ? (
                    <span>Best</span>
                  ) : (
                    <>
                      <span>DL1</span>
                      <span>DL2</span>
                      <span>DL3</span>
                    </>
                  )}
                </div>
              </div>
              <div className="run-sheet-cell total">Total</div>
            </div>
          </div>
        </div>

        <div className="run-sheet-scroll" onScroll={handleBodyScroll}>
          <div className="run-sheet-body">
            {visibleAthletes.map((athlete, index) => (
              <RunSheetRow
                key={athlete.key || `row-${index}`}
                athlete={athlete}
                liftCompletion={collapseFlags}
                liveAttemptIndex={liveAttemptIndex}
                currentAttemptKey={currentAttemptKey}
                onTogglePin={() =>
                  updateAthlete(athlete.key, (current) => ({
                    ...current,
                    runsheet: {
                      ...(current.runsheet || {}),
                      pinned: !current.runsheet?.pinned,
                    },
                  }))
                }
                onUpdateTarget={(value) =>
                  updateAthlete(athlete.key, (current) => ({
                    ...current,
                    runsheet: {
                      ...(current.runsheet || {}),
                      targetPosition: value,
                    },
                  }))
                }
                onUpdateAttempt={(lift, attemptIndex, updates) =>
                  updateAthlete(athlete.key, (current) => {
                    const runsheet = current.runsheet || {};
                    const updatedLift = (runsheet[lift] || []).map((attempt, idx) =>
                      idx === attemptIndex ? { ...attempt, ...updates } : attempt
                    );
                    return {
                      ...current,
                      runsheet: {
                        ...runsheet,
                        [lift]: updatedLift,
                      },
                    };
                  })
                }
              />
            ))}
          </div>
        </div>
      </section>

      {error ? <div className="error">{error}</div> : null}
    </div>
  );
}

function RunSheetRow({
  athlete,
  liftCompletion,
  liveAttemptIndex,
  currentAttemptKey,
  onTogglePin,
  onUpdateAttempt,
  onUpdateTarget,
}) {
  const runsheet = athlete.runsheet || {};
  const liveRank = athlete.actualRank ? `#${athlete.actualRank}` : "-";
  const predictedRank = athlete.predictedRank ? `#${athlete.predictedRank}` : "-";
  const displayName = formatAthleteName(athlete.name);
  const openPowerliftingId = getOpenPowerliftingId(athlete);
  const showAthleteLink = Boolean(openPowerliftingId && hasAthleteStats(athlete));
  const metaBits = [
    athlete.weightClassLabel,
    athlete.gender,
    athlete.team,
  ].filter(Boolean);
  const squatForecast = getLiftForecast("squat", runsheet.squat, athlete.stats);
  const benchForecast = getLiftForecast("bench", runsheet.bench, athlete.stats);
  const deadliftForecast = getLiftForecast("deadlift", runsheet.deadlift, athlete.stats);
  const compBestSquat = getCompBest(athlete.stats, "squat");
  const compBestBench = getCompBest(athlete.stats, "bench");
  const compBestDeadlift = getCompBest(athlete.stats, "deadlift");
  const nameKey = athlete.nameKey || normalizeNameKey(athlete.name);
  const getLiveAttempt = useCallback(
    (lift, index) => getLiveAttemptFor(liveAttemptIndex, nameKey, lift, index),
    [liveAttemptIndex, nameKey]
  );
  const isCurrentAttempt = useCallback(
    (lift, index) =>
      Boolean(
        currentAttemptKey &&
          currentAttemptKey.nameKey === nameKey &&
          currentAttemptKey.lift === lift &&
          currentAttemptKey.attempt === index + 1
      ),
    [currentAttemptKey, nameKey]
  );
  const targetPosition = runsheet.targetPosition ?? "";
  const showTarget = Boolean(targetPosition);

  const collapsed = liftCompletion || {};

  return (
    <>
      <div
        className={`run-sheet-row${runsheet.pinned ? " pinned" : ""}${
          athlete.isLastPinned ? " pinned-last" : ""
        }`}
      >
        <div className="run-sheet-cell rank">
          <div className="rank-chip pred">
            <span className="rank-key">pred</span>
            <span className="rank-value">{predictedRank}</span>
          </div>
          <div className="rank-chip live">
            <span className="rank-key">curr</span>
            <span className="rank-value">{liveRank}</span>
          </div>
        </div>

        <div className="run-sheet-cell athlete">
          <div className="athlete-main">
          <div className="athlete-pin-stack">
            <button
              type="button"
              className={`pin-icon ${runsheet.pinned ? "active" : ""}`}
              onClick={onTogglePin}
              aria-label={runsheet.pinned ? "Unpin lifter" : "Pin lifter"}
            >
              <span aria-hidden="true">📌</span>
            </button>
            {runsheet.pinned ? (
              <select
                className="target-select"
                value={targetPosition === null ? "" : String(targetPosition)}
                onChange={(event) => {
                  const value = event.target.value;
                  onUpdateTarget(value ? Number.parseInt(value, 10) : null);
                }}
                aria-label="Target position"
                title="Target position"
              >
                <option value="">--</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
              </select>
            ) : null}
          </div>
          <div>
            <h3 title={athlete.name || ""}>
              <span className="athlete-name-row">
                <span>{displayName}</span>
                {showAthleteLink ? (
                  <a
                    className="athlete-link"
                    href={`https://apa.staging.nattercan.com/athlete?id=${encodeURIComponent(
                      openPowerliftingId
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Open athlete stats"
                  >
                    ↗
                  </a>
                ) : null}
              </span>
            </h3>
            {metaBits.length ? (
              <div className="athlete-meta">{metaBits.join(" / ")}</div>
            ) : null}
          </div>
        </div>
      </div>

        <div className="run-sheet-cell lift">
          <div className="lift-label">Squat</div>
          {collapsed.squat ? (
            <div className="lift-grid compact">
              <AttemptCell
                attempt={athlete.bestSquatAttempt}
                prediction={null}
                liveAttempt={null}
                onUpdate={null}
                isCurrent={false}
                attemptIndex={null}
                previousWeight={null}
                compBest={compBestSquat}
                liftKey="squat"
                locked
              />
            </div>
          ) : (
            <div className="lift-grid">
              {runsheet.squat.map((attempt, idx) => (
                <AttemptCell
                  key={`squat-${idx}`}
                  attempt={attempt}
                  prediction={squatForecast.suggestions[idx]}
                  liveAttempt={getLiveAttempt("squat", idx)}
                  isCurrent={isCurrentAttempt("squat", idx)}
                  attemptIndex={idx}
                  previousWeight={
                    idx > 0 ? toNumberOrNull(runsheet.squat?.[idx - 1]?.weight) : null
                  }
                  compBest={compBestSquat}
                  liftKey="squat"
                  onUpdate={(updates) => onUpdateAttempt("squat", idx, updates)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="run-sheet-cell lift">
          <div className="lift-label">Bench</div>
          {collapsed.bench ? (
            <div className="lift-grid compact">
              <AttemptCell
                attempt={athlete.bestBenchAttempt}
                prediction={null}
                liveAttempt={null}
                onUpdate={null}
                isCurrent={false}
                attemptIndex={null}
                previousWeight={null}
                compBest={compBestBench}
                liftKey="bench"
                locked
              />
            </div>
          ) : (
            <div className="lift-grid">
              {runsheet.bench.map((attempt, idx) => (
                <AttemptCell
                  key={`bench-${idx}`}
                  attempt={attempt}
                  prediction={benchForecast.suggestions[idx]}
                  liveAttempt={getLiveAttempt("bench", idx)}
                  isCurrent={isCurrentAttempt("bench", idx)}
                  attemptIndex={idx}
                  previousWeight={
                    idx > 0 ? toNumberOrNull(runsheet.bench?.[idx - 1]?.weight) : null
                  }
                  compBest={compBestBench}
                  liftKey="bench"
                  onUpdate={(updates) => onUpdateAttempt("bench", idx, updates)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="run-sheet-cell subtotal">
          <div className="total-block">
            <div className="total-line">
              {showTarget ? (
                <span className={`total-delta ${getDeltaTone(athlete.targetDeltaSubtotalPred)}`}>
                  {formatDelta(athlete.targetDeltaSubtotalPred)}
                </span>
              ) : null}
              <span className="total-pred">{formatWeight(athlete.predictedSubtotal)}</span>
            </div>
            <span className="total-label">pred</span>
          </div>
          <div className="total-block">
            <div className="total-line">
              {showTarget ? (
                <span className={`total-delta ${getDeltaTone(athlete.targetDeltaSubtotalLive)}`}>
                  {formatDelta(athlete.targetDeltaSubtotalLive)}
                </span>
              ) : null}
              <span className="total-live">{formatWeight(athlete.actualSubtotal)}</span>
            </div>
            <span className="total-label">curr</span>
          </div>
        </div>

        <div className="run-sheet-cell lift">
          <div className="lift-label">Deadlift</div>
          {collapsed.deadlift ? (
            <div className="lift-grid compact">
              <AttemptCell
                attempt={athlete.bestDeadliftAttempt}
                prediction={null}
                liveAttempt={null}
                onUpdate={null}
                isCurrent={false}
                attemptIndex={null}
                previousWeight={null}
                compBest={compBestDeadlift}
                liftKey="deadlift"
                locked
              />
            </div>
          ) : (
            <div className="lift-grid">
              {runsheet.deadlift.map((attempt, idx) => (
                <AttemptCell
                  key={`deadlift-${idx}`}
                  attempt={attempt}
                  prediction={deadliftForecast.suggestions[idx]}
                  liveAttempt={getLiveAttempt("deadlift", idx)}
                  isCurrent={isCurrentAttempt("deadlift", idx)}
                  attemptIndex={idx}
                  previousWeight={
                    idx > 0 ? toNumberOrNull(runsheet.deadlift?.[idx - 1]?.weight) : null
                  }
                  compBest={compBestDeadlift}
                  liftKey="deadlift"
                  onUpdate={(updates) => onUpdateAttempt("deadlift", idx, updates)}
                />
              ))}
            </div>
          )}
        </div>

        <div className="run-sheet-cell total">
          <div className="total-block">
            <div className="total-line">
              {showTarget ? (
                <span className={`total-delta ${getDeltaTone(athlete.targetDeltaTotalPred)}`}>
                  {formatDelta(athlete.targetDeltaTotalPred)}
                </span>
              ) : null}
              <span className="total-pred">{formatWeight(athlete.predictedTotal)}</span>
            </div>
            <span className="total-label">pred</span>
          </div>
          <div className="total-block">
            <div className="total-line">
              {showTarget ? (
                <span className={`total-delta ${getDeltaTone(athlete.targetDeltaTotalLive)}`}>
                  {formatDelta(athlete.targetDeltaTotalLive)}
                </span>
              ) : null}
              <span className="total-live">{formatWeight(athlete.actualTotal)}</span>
            </div>
            <span className="total-label">curr</span>
          </div>
        </div>
      </div>
    </>
  );
}

function AttemptCell({
  attempt,
  prediction,
  liveAttempt,
  onUpdate,
  locked = false,
  isCurrent = false,
  attemptIndex = null,
  previousWeight = null,
  compBest = null,
  liftKey = "",
}) {
  const liveStatus = normalizeAttemptStatus(liveAttempt?.status);
  const isFinal = locked || liveStatus === "good" || liveStatus === "miss";
  const liveWeight = toNumberOrNull(liveAttempt?.weight);
  const manualOverride = Boolean(attempt.manual_override);
  const manualOverrideLive = toNumberOrNull(attempt.manual_override_live);
  const useManualOverride =
    manualOverride && liveWeight !== null && manualOverrideLive === liveWeight;
  let displayWeight = attempt.weight;
  if (isFinal && liveWeight !== null) {
    displayWeight = liveStatus === "miss" ? -Math.abs(liveWeight) : liveWeight;
  } else if (liveWeight !== null && !useManualOverride) {
    displayWeight = liveWeight;
  }
  let displayStatus = "pending";
  if (liveStatus) {
    displayStatus = liveStatus;
  } else if (locked && attempt.status) {
    displayStatus = attempt.status;
  }
  const tone =
    displayStatus === "pending" ? getProbabilityTone(attempt.probability) : "";
  const statusLabel =
    displayStatus === "pending" ? formatProbability(attempt.probability) : "";
  const showStatus = displayStatus === "pending" && !locked;
  const hasWeight =
    toNumberOrNull(displayWeight) !== null && toNumberOrNull(displayWeight) !== 0;
  const predictionValue = toNumberOrNull(prediction);
  const showPrediction = !hasWeight && predictionValue !== null && !isFinal;
  const predictionLabel = showPrediction ? formatWeight(predictionValue) : "";
  const numericWeight = toNumberOrNull(displayWeight);
  const isFailedWeight =
    numericWeight !== null && (numericWeight < 0 || displayStatus === "miss");
  const isSuccessWeight = displayStatus === "good";
  const hasInput =
    displayWeight !== null && displayWeight !== undefined && displayWeight !== "";
  const compBestValue = toNumberOrNull(compBest);
  const effectiveWeight = showPrediction ? predictionValue : numericWeight;
  const aboveCompBest =
    !isFinal &&
    effectiveWeight !== null &&
    effectiveWeight > 0 &&
    compBestValue !== null &&
    effectiveWeight > compBestValue;
  const aboveDelta = aboveCompBest ? effectiveWeight - compBestValue : null;
  const aboveDeltaLabel = aboveCompBest ? `+${formatWeight(aboveDelta)} pb` : "";
  const isFakeOpener =
    !isFinal &&
    hasInput &&
    isSuspiciousOpener(liftKey, attemptIndex, numericWeight, predictionValue);
  const isInvalidWeight =
    !isFinal &&
    hasInput &&
    numericWeight !== null &&
    !isValidAttemptWeight(numericWeight, attemptIndex, previousWeight);

  const cardStatus = displayStatus || "";
  const isComplete = displayStatus === "good" || displayStatus === "miss";

  return (
    <div
      className={`attempt-card${cardStatus ? ` ${cardStatus}` : ""}${
        isComplete ? " final" : ""
      }${isCurrent ? " current" : ""}`}
    >
      <div className="attempt-inputs">
        {isFakeOpener ? (
          <span className="attempt-fake" title="Above predicted opener">
            ?
          </span>
        ) : aboveCompBest ? (
          <span className="attempt-overshoot" title="Above comp best">
            {aboveDeltaLabel}
          </span>
        ) : null}
        <input
          className={`${isFailedWeight ? "failed" : ""}${isSuccessWeight ? " success" : ""}${
            isFinal ? " locked" : ""
          }${aboveCompBest ? " over-best" : ""}${isInvalidWeight ? " invalid" : ""}`}
          type="number"
          inputMode="decimal"
          value={displayWeight ?? ""}
          placeholder={showPrediction ? predictionLabel : ""}
          aria-label="Attempt weight"
          readOnly={isFinal}
          onChange={(event) => {
            if (isFinal || !onUpdate) return;
            const nextValue = event.target.value;
            onUpdate({
              weight: nextValue,
              manual_override: nextValue !== "",
              manual_override_live: nextValue !== "" ? liveWeight ?? null : null,
            });
          }}
          onBlur={() => {
            if (isFinal || !onUpdate) return;
            if (!isInvalidWeight) return;
            onUpdate({
              weight: "",
              manual_override: false,
              manual_override_live: null,
            });
          }}
        />
        {showStatus ? (
          <div className={`attempt-status${tone ? ` tone-${tone}` : ""}`}>
            {statusLabel}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default RunSheet;
